'use client'

/**
 * FormRender — the customer-facing form renderer.
 *
 * Consumed by:
 *   - apps/storefront/src/components/FormSections.tsx (live customer site)
 *   - the portal's website-builder preview pane (Phase 6) — same component
 *     so what the dealer sees while editing matches what visitors will get
 *
 * State: react-hook-form with a custom resolver that re-runs
 * `buildZodForRender(definition, currentValues)` on every validation pass,
 * so conditional `required-if` / `hide` rules behave identically to the
 * server-side check in `/api/forms/submit`.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useForm, type Resolver, type FieldValues } from 'react-hook-form'
import {
  buildZodForRender,
  evaluateConditions,
  isSafeRedirectTarget,
  type FieldDef,
  type FormDefinition,
  type FormValues,
  type SuccessBehavior,
} from '@sifly/forms'
import { US_STATE_CODES, type CountryCode } from '@sifly/shared-utils'
import PhoneInput from './LazyPhoneInput'
import CountryPicker from './CountryPicker'
import { useFormToken } from './useFormToken'
import { SignaturePad } from './SignaturePad'

// Maps a field to an HTML autocomplete token so mobile browsers can offer
// autofill (name / email / address). Phone autofill is handled inside
// PhoneInput (autoComplete="tel"). Deterministic key/label keyword match;
// returns undefined when there's no confident mapping (browser falls back to
// its own heuristics).
function autoCompleteFor(field: FieldDef): string | undefined {
  if (field.type === 'email') return 'email'
  if (field.type !== 'text' && field.type !== 'textarea') return undefined
  const hint = `${field.key} ${'label' in field ? field.label : ''}`
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
  if (/\b(first|given)\b/.test(hint)) return 'given-name'
  if (/\b(last|family|surname)\b/.test(hint)) return 'family-name'
  if (/\bname\b/.test(hint)) return 'name'
  if (/\b(company|organization|organisation|business|dealer)\b/.test(hint)) return 'organization'
  if (/\b(street|address)\b/.test(hint)) return 'street-address'
  if (/\b(city|town)\b/.test(hint)) return 'address-level2'
  if (/\b(state|province|region)\b/.test(hint)) return 'address-level1'
  if (/\b(zip|postal|postcode)\b/.test(hint)) return 'postal-code'
  return undefined
}

export interface FormRenderProps {
  definition: FormDefinition
  formId: string
  versionId: string
  /** Defaults to `/api/forms/submit` (Next.js storefront route). Override for testing. */
  submitUrl?: string
  /** Page slug for `lead_source` / `source_page_slug`. */
  sourcePageSlug?: string
  defaultCountry?: CountryCode
  favoriteCountries?: CountryCode[]
  /** Seed initial field values by field key (e.g. a message pre-filled from a
   *  PDP "Request a quote" link). Only keys that exist as fields in the
   *  definition are applied; unknown keys are ignored. Merged over the
   *  definition-derived defaults. */
  prefillValues?: Record<string, string>
  /** Called on a 200 response. Receives the server-confirmed success behavior. */
  onSuccess?: (behavior: SuccessBehavior) => void
  /** Real saved submission only; runs before redirect navigation. */
  onSubmitted?: (submissionId: string) => void
  /** HMAC-signed token from the server; pins formId+versionId+renderedAt
   *  and binds the honeypot key. Echoed back on submit so the API route
   *  can run time-trap + honeypot checks. */
  formToken?: string
  /** Per-render random hidden-field name. Bots fill it; humans never see it. */
  honeypotKey?: string
  /** Cloudflare Turnstile site key — when present (and the form's settings
   *  enable Turnstile) the widget mounts and a token is required on submit. */
  turnstileSiteKey?: string | null
  /** Optional analytics callback. Emits `form_view` on mount and `form_start`
   *  on first field interaction (once per mount).
   *  Storefront passes `(type, meta) => track(type, meta)`.
   *  Portal preview callers omit this prop — no analytics side-effects. */
  onEvent?: (type: string, meta?: Record<string, string>) => void
  /**
   * Analytics session id — only supplied when the visitor has granted consent
   * (use `peekSessionId()` from the storefront analytics module, which returns
   * null when no session exists rather than minting a new one).
   * Stamped onto the form_submissions row to enable popup/page → lead joins.
   */
  sessionId?: string | null
  /**
   * Attribution context forwarded verbatim to the submit body.
   * Computed by the storefront's AnalyticsFormRender wrapper.
   * Portal preview callers omit this — no attribution needed there.
   */
  attributionContext?: {
    landingPage?: string
    referrer?: string
    popupId?: string
    consentState?: string
  }
}

// ─── Custom RHF resolver ──────────────────────────────────────────────────
// Re-builds the runtime zod schema on every validation pass against the
// current values, so required-if / hide rules update as the user types.

function createDynamicResolver(definition: FormDefinition): Resolver<FieldValues> {
  return async (values) => {
    const schema = buildZodForRender(definition, values as FormValues)
    const parsed = schema.safeParse(values)
    if (parsed.success) {
      return { values: parsed.data as FieldValues, errors: {} }
    }
    const errors: Record<string, { type: string; message: string }> = {}
    for (const issue of parsed.error.issues) {
      const path = issue.path.join('.')
      if (!errors[path]) {
        errors[path] = { type: 'validation', message: issue.message }
      }
    }
    return { values: {}, errors }
  }
}

// ─── Default-values helper ───────────────────────────────────────────────
// Pre-populates the RHF form with each field's `defaultValue`. Missing
// defaults map to '' (text), [] (multiselect), or false (checkbox / consent)
// so RHF gets a stable shape.

function initialValues(definition: FormDefinition, defaultCountry?: CountryCode): FormValues {
  const out: FormValues = {}
  for (const f of definition.fields) {
    switch (f.type) {
      case 'multiselect':
        out[f.key] = (f.defaultValue ?? []) as string[]
        break
      case 'checkbox':
      case 'consent':
        out[f.key] = Boolean(f.type === 'checkbox' ? f.defaultValue : false)
        break
      case 'signature':
        // The value is a PNG data URL set on first stroke; starts empty.
        out[f.key] = ''
        break
      case 'country':
        // Seed with the field's own default, else the dealer's default country
        // (tenants.favorite_countries[0]) so the form lands pre-filled.
        out[f.key] = (f.defaultValue ?? defaultCountry ?? '') as string
        break
      case 'number':
        out[f.key] = (f.defaultValue ?? '') as never
        break
      default:
        out[f.key] = (f.defaultValue ?? '') as string
    }
  }
  return out
}

// ─── Field renderer ──────────────────────────────────────────────────────

interface FieldRendererProps {
  field: FieldDef
  required: boolean
  value: unknown
  error: string | undefined
  onChange: (next: unknown) => void
  defaultCountry?: CountryCode
  favoriteCountries?: CountryCode[]
  /** Current value of the form's `country` field — drives the country-aware
   *  `region` field (US → state dropdown, else free text). */
  selectedCountry?: string
}

function FieldRenderer({
  field,
  required,
  value,
  error,
  onChange,
  defaultCountry,
  favoriteCountries,
  selectedCountry,
}: FieldRendererProps) {
  const inputCls =
    'block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 text-base shadow-sm focus:border-[var(--color-primary,#000)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary,#000)]'
  const errorCls = 'mt-1 text-xs text-[var(--color-error-strong)]'

  // Stable id so each field's error message can be referenced by its control
  // via aria-describedby (WCAG 3.3.1). useId keeps it unique even when two
  // forms with the same field key render on one page.
  const reactId = useId()
  const errorId = error ? `${reactId}-error` : undefined

  // Spread onto every single-control input so assistive tech announces the
  // field as invalid and points at the matching error message.
  const invalidProps = {
    'aria-invalid': error ? true : undefined,
    'aria-describedby': errorId,
  }

  // The shared inline label + help-text + error UI for non-checkbox fields.
  // `labelOverride` lets the country-aware region field show a generic label
  // ("State / Province / Region") for non-US countries.
  const wrap = (control: React.ReactNode, labelOverride?: string) => (
    <div className="mb-4">
      {field.type !== 'hidden' && (
        <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
          {labelOverride ?? (field as { label: string }).label}
          {required && <span className="ml-0.5 text-[var(--color-error-strong)]">*</span>}
        </label>
      )}
      {control}
      {field.type !== 'hidden' &&
        'helpText' in field &&
        field.helpText && (
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">{field.helpText}</p>
        )}
      {error && <p id={errorId} role="alert" className={errorCls}>{error}</p>}
    </div>
  )

  switch (field.type) {
    case 'text':
      return wrap(
        <input
          type="text"
          className={inputCls}
          autoComplete={autoCompleteFor(field)}
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          maxLength={field.maxLength}
          {...invalidProps}
        />,
      )
    case 'email':
      return wrap(
        <input
          type="email"
          className={inputCls}
          autoComplete="email"
          inputMode="email"
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          {...invalidProps}
        />,
      )
    case 'phone':
      return wrap(
        <PhoneInput
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v)}
          defaultCountry={(field.defaultCountry as CountryCode | undefined) ?? defaultCountry ?? 'US'}
          favoriteCountries={favoriteCountries}
          required={required}
          invalid={error ? true : undefined}
          describedById={errorId}
        />,
      )
    case 'country':
      return wrap(
        <CountryPicker
          value={(value as string) ?? ''}
          onChange={(code) => onChange(code)}
          favorites={favoriteCountries as string[] | undefined}
          placeholder={field.placeholder ?? 'Country'}
          inputClassName={inputCls}
        />,
      )
    case 'region': {
      // Country-aware: US → state dropdown; otherwise a generic free-text field
      // with a widened label, mirroring the portal's order/contact address form.
      const isUS = (selectedCountry ?? '') === 'US'
      if (isUS) {
        return wrap(
          <select
            className={inputCls}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            {...invalidProps}
          >
            <option value="">{field.placeholder ?? 'State'}</option>
            {US_STATE_CODES.slice().sort().map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>,
        )
      }
      return wrap(
        <input
          type="text"
          className={inputCls}
          autoComplete="address-level1"
          placeholder="State / Province / Region"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          {...invalidProps}
        />,
        'State / Province / Region',
      )
    }
    case 'number':
      return wrap(
        <input
          type="number"
          className={inputCls}
          placeholder={field.placeholder}
          value={(value as string | number | undefined) ?? ''}
          min={field.min}
          max={field.max}
          step={field.step}
          onChange={(e) => onChange(e.target.value)}
          {...invalidProps}
        />,
      )
    case 'textarea':
      return wrap(
        <textarea
          className={inputCls}
          autoComplete={autoCompleteFor(field)}
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          rows={field.rows}
          onChange={(e) => onChange(e.target.value)}
          maxLength={field.maxLength}
          {...invalidProps}
        />,
      )
    case 'select':
      return wrap(
        <select
          className={inputCls}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          {...invalidProps}
        >
          <option value="">{field.placeholder ?? 'Select…'}</option>
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>,
      )
    case 'multiselect': {
      const selected = Array.isArray(value) ? (value as string[]) : []
      return wrap(
        <div className="space-y-1" role="group" {...invalidProps}>
          {field.options.map((o) => {
            const checked = selected.includes(o.value)
            return (
              <label key={o.value} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-[var(--color-border)]"
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...selected, o.value])
                    else onChange(selected.filter((v) => v !== o.value))
                  }}
                />
                <span>{o.label}</span>
              </label>
            )
          })}
        </div>,
      )
    }
    case 'radio':
      return wrap(
        <div className="space-y-1" role="radiogroup" {...invalidProps}>
          {field.options.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={field.key}
                className="h-4 w-4 border-[var(--color-border)]"
                checked={value === o.value}
                onChange={() => onChange(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>,
      )
    case 'checkbox':
      return (
        <div className="mb-4">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-[var(--color-border)]"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              {...invalidProps}
            />
            <span>
              {field.label}
              {required && <span className="ml-0.5 text-[var(--color-error-strong)]">*</span>}
              {field.helpText && (
                <span className="block text-xs text-[var(--color-text-muted)]">{field.helpText}</span>
              )}
            </span>
          </label>
          {error && <p id={errorId} role="alert" className={errorCls}>{error}</p>}
        </div>
      )
    case 'consent':
      return (
        <div className="mb-4">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-[var(--color-border)]"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              {...invalidProps}
            />
            <span>{field.consentText}</span>
          </label>
          {error && <p id={errorId} role="alert" className={errorCls}>{error}</p>}
        </div>
      )
    case 'date':
      return wrap(
        <input
          type="date"
          className={inputCls}
          value={(value as string) ?? ''}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(e.target.value)}
          {...invalidProps}
        />,
      )
    case 'signature':
      return wrap(
        <SignaturePad
          onChange={(next) => onChange(next)}
          invalidProps={invalidProps}
        />,
      )
    case 'hidden':
      return (
        <input
          type="hidden"
          name={field.key}
          value={(value as string) ?? field.defaultValue ?? ''}
          readOnly
        />
      )
  }
}

// ─── Main component ──────────────────────────────────────────────────────

export function FormRender({
  definition,
  formId,
  versionId,
  submitUrl = '/api/forms/submit',
  sourcePageSlug,
  defaultCountry = 'US',
  favoriteCountries,
  prefillValues,
  onSuccess,
  onSubmitted,
  formToken: formTokenProp,
  honeypotKey: honeypotKeyProp,
  turnstileSiteKey,
  onEvent,
  sessionId,
  attributionContext,
}: FormRenderProps) {
  const defaults = useMemo(() => {
    const base = initialValues(definition, defaultCountry)
    // Merge caller-supplied prefills, but ONLY for keys that are real fields in
    // this definition (so an unknown key can't inject a phantom value).
    if (prefillValues) {
      for (const [k, v] of Object.entries(prefillValues)) {
        if (k in base && typeof v === 'string') base[k] = v
      }
    }
    return base
  }, [definition, defaultCountry, prefillValues])
  const resolver = useMemo(() => createDynamicResolver(definition), [definition])

  // Anti-spam token. Server-minted tokens are no longer passed on the cacheable
  // storefront (they expire, so they can't live in cached HTML — see
  // useFormToken); the browser fetches its own. The `formToken` prop is still
  // honoured for any caller that renders per-request and supplies one.
  const { pair: fetchedToken, ensureToken } = useFormToken({
    formId,
    enabled: !formTokenProp,
  })
  const formToken = formTokenProp ?? fetchedToken?.formToken
  const honeypotKey = honeypotKeyProp ?? fetchedToken?.honeypotKey

  // The form's country field (if any) drives the country-aware region field.
  const countryFieldKey = useMemo(
    () => definition.fields.find((f) => f.type === 'country')?.key,
    [definition],
  )

  const {
    register: _register, // unused — we use Controller-style manual wiring per field
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    trigger,
  } = useForm<FieldValues>({
    resolver,
    defaultValues: defaults,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  void _register

  const currentValues = watch() as FormValues
  const visibility = useMemo(
    () => evaluateConditions(definition, currentValues),
    [definition, currentValues],
  )

  // Analytics: form_view on mount, form_start on first field interaction.
  // onEventRef keeps the callbacks stable across renders so the mount
  // effect can depend only on [] without stale-closure risk.
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent
  const formIdRef = useRef(formId)
  formIdRef.current = formId
  const sourcePageSlugRef = useRef(sourcePageSlug)
  sourcePageSlugRef.current = sourcePageSlug
  const formStarted = useRef(false)
  useEffect(() => {
    onEventRef.current?.('form_view', { form_id: formIdRef.current, location: sourcePageSlugRef.current ?? '' })
  }, [])
  function trackFormStart() {
    if (formStarted.current) return
    formStarted.current = true
    onEventRef.current?.('form_start', { form_id: formIdRef.current })
  }

  const steps = definition.steps
  const isWizard = steps.length > 1
  const [stepIdx, setStepIdx] = useState(0)
  // Memoized so its identity is stable across renders — goNext's useCallback
  // depends on it, and a fresh array each render would bust that callback.
  const currentStepKeys = useMemo(
    () => (isWizard ? steps[stepIdx]?.fieldKeys ?? [] : null),
    [isWizard, steps, stepIdx],
  )

  const fieldsToRender = definition.fields.filter((f) => {
    if (visibility[f.key]?.visible === false) return false
    if (!isWizard) return true
    return currentStepKeys?.includes(f.key) ?? false
  })

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<{ title: string; body?: string } | null>(null)

  // Honeypot: bots fill every visible-looking input. Stored in component
  // state (not RHF) so it's never validated against the schema and never
  // surfaces to a real user.
  const [honeypotValue, setHoneypotValue] = useState('')

  // Turnstile widget mount + token capture. Loads the script once per
  // page, renders the widget into an absolute-positioned container.
  const turnstileContainer = useRef<HTMLDivElement | null>(null)
  const [turnstileToken, setTurnstileToken] = useState('')
  useEffect(() => {
    if (!turnstileSiteKey) return
    const scriptId = 'cf-turnstile-script'
    if (!document.getElementById(scriptId)) {
      const s = document.createElement('script')
      s.id = scriptId
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      s.async = true
      s.defer = true
      document.head.appendChild(s)
    }
    let cancelled = false
    function tryRender(): boolean {
      const w = (window as unknown as {
        turnstile?: {
          render: (
            el: HTMLElement,
            opts: { sitekey: string; callback: (token: string) => void; 'error-callback'?: () => void },
          ) => string
        }
      }).turnstile
      if (!w || !turnstileContainer.current || cancelled) return false
      w.render(turnstileContainer.current, {
        sitekey: turnstileSiteKey!,
        callback: (token) => {
          if (!cancelled) setTurnstileToken(token)
        },
        'error-callback': () => {
          if (!cancelled) setTurnstileToken('')
        },
      })
      return true
    }
    if (!tryRender()) {
      const iv = setInterval(() => {
        if (tryRender()) clearInterval(iv)
      }, 200)
      return () => {
        cancelled = true
        clearInterval(iv)
      }
    }
    return () => {
      cancelled = true
    }
  }, [turnstileSiteKey])

  const goNext = useCallback(async () => {
    // Only validate the current step's fields before advancing.
    if (!currentStepKeys) return
    const ok = await trigger(currentStepKeys)
    if (ok) setStepIdx((i) => Math.min(i + 1, steps.length - 1))
  }, [currentStepKeys, trigger, steps.length])

  const goBack = useCallback(() => {
    setStepIdx((i) => Math.max(i - 1, 0))
  }, [])

  const onSubmit = useCallback(
    async (values: FieldValues) => {
      setSubmitError(null)
      if (turnstileSiteKey && !turnstileToken) {
        setSubmitError('Please complete the verification challenge.')
        return
      }
      // A submit can land before the on-mount token fetch resolves (fast
      // autofill, slow network). Posting without a token is rejected as
      // `invalid_token` and SILENTLY dropped, so wait for it rather than
      // losing a real submission.
      const resolvedToken = formToken ?? (await ensureToken())?.formToken
      try {
        const res = await fetch(submitUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            formId,
            versionId,
            data: values,
            sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
            sourcePageSlug,
            formToken: resolvedToken,
            // Always sent: the server treats a missing honeypot as a warning
            // sign (a held lead), so leaving it out only delays real leads.
            honeypot: honeypotValue,
            turnstileToken: turnstileSiteKey ? turnstileToken : undefined,
            sessionId: sessionId ?? undefined,
            landingPage: attributionContext?.landingPage,
            referrer: attributionContext?.referrer,
            popupId: attributionContext?.popupId,
            consentState: attributionContext?.consentState,
          }),
        })
        if (!res.ok) {
          const j = (await res.json().catch(() => ({}))) as { error?: string }
          setSubmitError(j.error ?? 'Submission failed. Please try again.')
          return
        }
        const j = (await res.json()) as { ok: true; successBehavior: SuccessBehavior; submissionId?: string }
        if (j.submissionId) onSubmitted?.(j.submissionId)
        if (j.successBehavior.kind === 'redirect') {
          // The one place a stored redirect is FOLLOWED, so it is checked here
          // too, not only where definitions are parsed: whatever reaches
          // `location.href` runs as navigation — or, for `javascript:`, as
          // script on the dealer's own site. A value that fails the allowlist
          // gets the plain thank-you instead of being followed. The
          // submission is already saved either way.
          const url = j.successBehavior.url
          if (isSafeRedirectTarget(url)) {
            window.location.href = url
            return
          }
          setSuccessMessage({ title: 'Thanks!' })
          onSuccess?.({ kind: 'message', title: 'Thanks!' })
          return
        }
        setSuccessMessage({ title: j.successBehavior.title, body: j.successBehavior.body })
        onSuccess?.(j.successBehavior)
      } catch {
        setSubmitError('Network error. Please try again.')
      }
    },
    [
      formId,
      versionId,
      submitUrl,
      sourcePageSlug,
      onSuccess,
      onSubmitted,
      formToken,
      ensureToken,
      honeypotValue,
      turnstileSiteKey,
      turnstileToken,
      sessionId,
      attributionContext,
    ],
  )

  if (successMessage) {
    return (
      <div className="rounded-md border border-[var(--color-success-border)] bg-[var(--color-success-surface)] p-6 text-center">
        <p className="text-lg font-semibold text-[var(--color-success-strong)]">{successMessage.title}</p>
        {successMessage.body && <p className="mt-1 text-sm text-[var(--color-success-strong)]">{successMessage.body}</p>}
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-1">
      {isWizard && (
        <div className="mb-4 flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
          {steps.map((s, i) => (
            <span
              key={s.key}
              className={`rounded-full px-2 py-0.5 ${
                i === stepIdx ? 'bg-[var(--color-primary)] text-[var(--color-primary-text)]' : 'bg-[var(--color-surface-muted)]'
              }`}
            >
              {s.title}
            </span>
          ))}
        </div>
      )}

      {fieldsToRender.map((field) => {
        const fieldErr = errors[field.key]?.message
        const required = visibility[field.key]?.required ?? false
        return (
          <FieldRenderer
            key={field.key}
            field={field}
            required={required}
            value={currentValues[field.key]}
            error={typeof fieldErr === 'string' ? fieldErr : undefined}
            onChange={(next) => {
              trackFormStart()
              setValue(field.key, next as never, { shouldValidate: false })
            }}
            defaultCountry={defaultCountry}
            favoriteCountries={favoriteCountries}
            selectedCountry={countryFieldKey ? (currentValues[countryFieldKey] as string | undefined) : undefined}
          />
        )
      })}

      {/* Honeypot — invisible to humans, irresistible to naïve bots. */}
      {honeypotKey && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          <label>
            Website (leave blank)
            <input
              type="text"
              name={honeypotKey}
              tabIndex={-1}
              autoComplete="off"
              value={honeypotValue}
              onChange={(e) => setHoneypotValue(e.target.value)}
            />
          </label>
        </div>
      )}

      {turnstileSiteKey && (
        <div className="my-3">
          <div ref={turnstileContainer} />
        </div>
      )}

      {submitError && (
        <div className="mb-2 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-surface)] p-3 text-sm text-[var(--color-error-strong)]">
          {submitError}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        {isWizard && stepIdx > 0 ? (
          <button
            type="button"
            onClick={goBack}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
          >
            Back
          </button>
        ) : (
          <span />
        )}

        {isWizard && stepIdx < steps.length - 1 ? (
          <button
            type="button"
            onClick={goNext}
            className="rounded-md bg-[var(--color-cta,#000)] px-4 py-2 text-sm font-medium text-[var(--color-cta-text,#fff)] hover:opacity-90"
          >
            Next
          </button>
        ) : (
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-[var(--color-cta,#000)] px-4 py-2 text-sm font-medium text-[var(--color-cta-text,#fff)] hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? 'Sending…' : definition.settings.submitLabel}
          </button>
        )}
      </div>
    </form>
  )
}
